import { Box, Button, Grid, Header, Input, Select, SpaceBetween, Spinner } from "@cloudscape-design/components"
import ExtensionCard from "./ExtensionCard"
import { useEffect, useMemo, useState } from "react";
import { ExtensionObjectType } from "types/extensionTypes";
import { OptionDefinition } from "@cloudscape-design/components/internal/components/option/interfaces";
import { useCluster } from "contexts/ClusterContext";
import deploymentService from "services/deploymentService";
import { useNotification } from "contexts/NotificationContext";
import InstallationParametersModal from "./InstallationParametersModal";
import ActionConfirmationModal from "components/shared/ActionConfirmationModal";




function BrowseExtensionsTab({ refreshKey }: { refreshKey: number }) {
  const [extensionsList, setExtensionsList] = useState<ExtensionObjectType[]>([]);
  const [loadingExtensions, setLoadingExensions] = useState(false);
  const [errorLoadingExtensions, setErrorLoadingExtensions] = useState<string | null>(null);

  const [categoriesList, setCategoriesList] = useState<OptionDefinition[]>([]);
  const [extensionSearch, setExtensionSearch] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<OptionDefinition>({ label: "All Categories", value: "all" });

  const { selectedCluster } = useCluster();

  const { addNotification } = useNotification();

  const [selectedExtension, setSelectedExtension] = useState<ExtensionObjectType | null>(null);

  const [showInstallModal, setShowInstallModal] = useState(false);

  const [showInstallConfirmationModal, setShowInstallConfirmationModal] = useState(false);

  const [installationParameters, setInstallationParameters] = useState<Record<string, string | boolean> | null>(null);

  const [loadingInstallation, setLoadingInstallation] = useState(false);

  

  const fetchExtensions = async ()=>{
    try {
      setLoadingExensions(true);
      const response = await deploymentService.getAllExtensions(selectedCluster?.value, "community", "all");
      
      setExtensionsList(response);

      const categories = Array.from(
        new Set<string>(
          response.map(
            (ext: ExtensionObjectType) => ext.metadata.category
          )
        )
      );

      const extensionCategoriesList: OptionDefinition[] = [
        { label: "All Categories", value: "all" },
        ...categories.map((category) => ({
          label: category,
          value: category,
        })),
      ];

      setCategoriesList(extensionCategoriesList);

    } catch (error) {
      setErrorLoadingExtensions("Failed to load extensions");
      console.error("Failed to Load Extensions", error);
    } finally{
      setLoadingExensions(false);
    }
  }


  useEffect(()=>{
    fetchExtensions();
  },[selectedCluster?.value, refreshKey]);




  const openParametersModal = (extension: ExtensionObjectType)=>{
    setSelectedExtension(extension);
    
    if(extension.parametersSchema){
      setShowInstallModal(true);
    }else{
      openConfirmationModal();
    }
  }



  const closeParametersModal = ()=>{
    setInstallationParameters(null);
    setShowInstallModal(false);
  }



  const confirmParametersModal = (parameters:Record<string, any> |null)=>{
    setInstallationParameters(parameters);
    openConfirmationModal();
  }

  


  const openConfirmationModal = ()=>{
    setShowInstallConfirmationModal(true);
  }



  const closeConfirmationModal = ()=>{
    // Close only the confirmation modal. Keep installation parameters and the parameters modal state intact so the user can continue editing if they dismissed the confirmation.
    setShowInstallConfirmationModal(false);
  }



  const confirmConfirmationModal = async ()=>{
    if(!selectedExtension) return;

    try {
      await installExtension(selectedExtension, installationParameters);
      // After successful installation, close both modals and clear state
      setShowInstallConfirmationModal(false);
      setShowInstallModal(false);
      setInstallationParameters(null);
      setSelectedExtension(null);
    } catch (error) {
      throw error;
    }
  }




  const installExtension = async(extension: ExtensionObjectType, parameters:Record<string, string|boolean> | null)=>{
    setLoadingInstallation(true);
    try {
      await deploymentService.installExtension(selectedCluster?.value, extension.id, parameters);
      fetchExtensions();
      addNotification("success", `${extension.displayName} installation has started.`);
      
    } catch (error) {
      console.error(`Failed to Install ${extension.displayName}`, error);
      addNotification("error", `Failed to install ${extension.displayName}`);
      
      throw error;
      
    } finally {
      setLoadingInstallation(false);
    }
  }



  // Derived state: Automatically re-calculates whenever search OR category changes
  const filteredExtensions = useMemo(() => {
    return extensionsList.filter((extension) => {
      // 1. Matches Search?
      const matchesSearch = extension.displayName
        .toLowerCase()
        .includes(extensionSearch.trim().toLowerCase());

      // 2. Matches Category?
      const matchesCategory =
        selectedCategory.value === "all" ||
        extension.metadata.category === selectedCategory.value;

      // Must match BOTH conditions
      return matchesSearch && matchesCategory;
    });
  }, [extensionsList, extensionSearch, selectedCategory]);


  // 1. LOADING STATE
  if (loadingExtensions) {
    return (
      <Box textAlign="center" padding={{ vertical: "xxl" }}>
        <SpaceBetween size="m">
          <Spinner size="large" />
          <Box color="text-body-secondary">Loading available extensions...</Box>
        </SpaceBetween>
      </Box>
    );
  }

  // 2. ERROR STATE
  if (errorLoadingExtensions) {
    return (
      <Box textAlign="center" padding={{ vertical: "xxl" }}>
        <SpaceBetween size="m">
          <Box variant="h3" color="text-status-error">{errorLoadingExtensions}</Box>
          <Button onClick={fetchExtensions} iconName="refresh">Retry</Button>
        </SpaceBetween>
      </Box>
    );
  }

  // 3. EMPTY STATE
  if (extensionsList.length === 0) {
    return (
      <Box textAlign="center" padding={{ vertical: "xxl" }}>
        <SpaceBetween size="m">
          <Box variant="h3">No extensions available</Box>
          <Box color="text-body-secondary">
            There are currently no extensions in the catalog.
          </Box>
        </SpaceBetween>
      </Box>
    );
  }
  
  return (
    <SpaceBetween size="xl">
      <SpaceBetween size="s" direction="horizontal">
        <Input 
          value={extensionSearch} 
          onChange={({detail})=>setExtensionSearch(detail.value)} 
          type="search" 
          placeholder="Search extensions..." 
          className="extensions-search-input"
        />

        <Select
          selectedOption={selectedCategory}
          onChange={({ detail }) => setSelectedCategory(detail.selectedOption)}
          options={categoriesList}
        />
      </SpaceBetween>

      <SpaceBetween size="xl">
        <div className="header-container">
          <Header variant="h3">
            Available Extensions
          </Header>
        </div>

        {filteredExtensions.length === 0 ?
        (
          <Box margin={{ vertical: "xs" }} textAlign="center" color="inherit">
            <SpaceBetween size="m">
              <div>
                <b>No extensions found</b>
                <Box variant="p" color="text-body-secondary">
                  We couldn't find any extensions matching your search or filters. Try adjusting your search term or clearing selected categories.
                </Box>
              </div>
              <Button onClick={() => { setExtensionSearch(""); setSelectedCategory(categoriesList[0]); }}>
                Clear filters
              </Button>
            </SpaceBetween>
          </Box>
        ):(
          <Grid
            gridDefinition={filteredExtensions.map(()=>({ colspan: { default: 12, s: 4, m: 3, l: 3 } }))}
          >
            {filteredExtensions.map((extension, index)=> 
              <ExtensionCard 
                key={index} 
                extension={extension} 
                viewMode="browse-tab" 
                onInstall={openParametersModal} 
              />
            )}
          </Grid>
        )}
      </SpaceBetween>

      <InstallationParametersModal
        visible={showInstallModal}
        extension={selectedExtension}
        onDismiss={closeParametersModal}
        onConfirm={confirmParametersModal}
      />

      <ActionConfirmationModal
        isVisible={showInstallConfirmationModal}
        isLoading={loadingInstallation}
        onDismiss={closeConfirmationModal}
        onConfirm={confirmConfirmationModal}
      >
        Are you sure you want to install{" "}
        <strong>{selectedExtension?.displayName}</strong>{" "}
        on{" "}
        <strong>{selectedCluster?.label}</strong> ?
      </ActionConfirmationModal>
    </SpaceBetween>
  )
}

export default BrowseExtensionsTab
